/**
 * cloudRuntimeRowState.logic.ts — pure logic for a single cloud row's visual
 * status and rendering state.
 *
 * Extracted from `cloudProjectsGroup.logic.ts`: `runtimeStatusToDotVariant`,
 * the resolvedRef derivations, and `resolveCloudGroupState` answer "what
 * does this ONE row/group look like right now", which shares no state or
 * helper functions with the identity/grouping cluster that stayed behind
 * (deriving cloud/local keys, merging catalog rows into sidebar projects).
 *
 * `resolveCloudRowProjectRefForIdentity` — WHICH Checkout a row names, once
 * its Environment has connected — moved to `cmmd/cmmdCheckoutResolution.ts`,
 * the module that owns Checkout resolution end to end. The directory-naming
 * helpers it used (`cloudRowWorkspaceDirectoryName`, `namedWorkspaceRoot`,
 * `normalizePath`, `directoryNameOf`) stayed here: `cloudCatalogRowSelection.ts`
 * reads them independently for the same directory convention, so moving them
 * too would have meant importing this file's exports back out of `cmmd/`.
 *
 * No React imports — every function is a pure transform.
 */
import type {
  CmmdRegistration,
  EnvironmentId,
  ProjectId,
  RepositoryIdentity,
} from "@cmmd-forge/contracts";
import type { CmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { ForgeRuntimeStatus } from "../../cmmd/forgeRuntimeStatus";
import { buildCmmdRuntimeEnvironmentId } from "../../environments/cmmdRuntimeEnvironmentId";

// ── Runtime status → dot variant ───────────────────────────────────────────────

/**
 * Visual dot variant driven by ForgeRuntimeStatus.
 *
 * Mapping:
 *   registered   → idle (no machine exists — must not read as stopped/error)
 *   running      → green
 *   provisioning → amber-pulse
 *   starting     → amber-pulse
 *   stopped      → grey (holds a Forge Cloud warm-pool slot until Destroyed)
 *   draining     → grey (holds a Forge Cloud warm-pool slot until Destroyed)
 *   error        → red-pulse
 *   destroyed    → idle (frees the slot; semantically identical to
 *                  `registered` — no machine, no slot held. User can
 *                  reprovision, same as any never-provisioned project.)
 *
 * Forge Cloud runtimes are per-(org, user, project), and the org has a hard
 * cap on warm slots. `stopped` (Suspended) and `draining` (Updating) both
 * KEEP the slot — only Destroy frees it. `destroyed` must therefore share the
 * hollow/idle treatment with `registered`, NOT the filled `grey` dot used by
 * the two slot-holding states, or the dot lies about which rows cost capacity.
 */
export type RuntimeDotVariant = "green" | "amber-pulse" | "grey" | "red-pulse" | "idle" | "hidden";

export function runtimeStatusToDotVariant(status: ForgeRuntimeStatus): RuntimeDotVariant {
  switch (status) {
    case "running":
      return "green";
    case "provisioning":
    case "starting":
      return "amber-pulse";
    // A registered project has no machine at all. `grey` is the "stopped VM"
    // colour and would claim infrastructure exists; `idle` is its own hollow
    // variant meaning "nothing running, nothing wrong".
    case "registered":
      return "idle";
    // Both HOLD a Forge Cloud warm-pool slot until the user explicitly
    // Destroys the runtime — `grey` (filled) correctly signals "a machine
    // exists" for these two.
    case "stopped":
    case "suspended":
    case "draining":
      return "grey";
    // Destroy is the ONLY action that frees the slot. A destroyed runtime
    // holds nothing, so it gets the SAME hollow `idle` dot as `registered`
    // rather than the `grey` used by slot-holding states above.
    case "destroyed":
      return "idle";
    case "error":
      return "red-pulse";
    default: {
      // Exhaustive — TypeScript ensures this is never reached, but the runtime
      // may receive future CMMD status strings.
      const _exhaustive: never = status;
      void _exhaustive;
      return "grey";
    }
  }
}

// ── resolvedRef derivation (identity-on-connect, D1=A) ────────────────────────

/**
 * A minimal slice of the zustand environment state needed to derive the
 * VM-local ProjectId once a cloud runtime has connected.
 *
 * The full `EnvironmentState` is intentionally avoided here so this helper
 * remains testable without importing the entire store.
 */
export interface CloudEnvironmentProjectSlice {
  readonly projectIds: readonly string[];
  readonly projectById: Readonly<
    Record<
      string,
      {
        readonly id: string;
        readonly cwd?: string;
        readonly repositoryIdentity?: RepositoryIdentity | null;
        /**
         * The Registration this Checkout was created from, when it was created
         * from one. Absent for a project added by hand and for every project
         * created before the field existed.
         */
        readonly cmmdRegistration?: CmmdRegistration | null;
      }
    >
  >;
}

/**
 * The identity a catalog row carries about WHICH project it names.
 */
export interface CloudRowProjectIdentity {
  /**
   * The row's Registration. Every catalog row carries one, and it is the only
   * thing that can name a Checkout on a shared Environment, where each
   * connected project reports an empty `cwd`.
   */
  readonly cmmdOrganizationId: number;
  readonly cmmdProjectId: number;
  readonly githubRepoFullName: string | null;
  readonly projectName: string;
  /**
   * What CMMD stores for the runtime, which is USUALLY the directory its
   * bootstrap seeded and sometimes the sprite's own root.
   *
   * The seeded name comes from the CMMD project TITLE
   * (`projectWorkspaceDirName`, forge-runtime-sprites-provider.ts), so a
   * project whose title does not slug to its repository name lives in a
   * directory the clone convention below cannot name. Read live on 2026-09-03:
   * project 198 reports `/projects/forge`.
   *
   * Older runtimes report a root instead: the guest home from the sprites
   * provider, `<root>/project-<cmmdProjectId>` when the admin inventory
   * rebuilds it. A single-segment path is one of those and is ignored.
   */
  readonly workspaceRoot: string | null;
}

/**
 * The directory name a row's workspace lands in on the runtime.
 *
 * Both server routes that create a cloud workspace derive the directory from
 * the row, so the row can derive it back:
 *   - a repo row → `resolveGitHubCloneDir`, `<root>/Projects/<repo name>`
 *   - a repo-less row → `makeCloudProjectPath`, `<root>/<slug of the name>`
 *
 * Only the last segment is compared. The two routes read different roots
 * (`baseDir` vs `cloudProjectsRoot`, the server default vs whatever
 * `FORGE_CLOUD_PROJECTS_ROOT` sets on a sprite), and the root says nothing
 * about which project a directory belongs to.
 */
export function cloudRowWorkspaceDirectoryName(identity: CloudRowProjectIdentity): string {
  const repo = identity.githubRepoFullName;
  if (repo) return repo.split("/")[1] ?? repo;
  return slugifyCloudProjectName(identity.projectName);
}

/**
 * `slugifyCloudProjectName` from the server's `POST /api/cloud/projects`.
 *
 * The quotes are stripped with two `replaceAll` calls rather than the server's
 * `/['"]/g`. A lone quote inside a regex character class reads as the start of
 * a string to the scanner in `cmmdRuntimeEnvironmentId.test.ts`, which then
 * swallows this file's JSDoc and reports it for re-deriving `cmmd-runtime:`.
 * Same output, both quote characters removed before the rest of the rules run.
 */
function slugifyCloudProjectName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replaceAll("'", "")
    .replaceAll('"', "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug.length > 0 ? slug : "project";
}

export function normalizePath(cwd: string | undefined): string | null {
  if (!cwd) return null;
  const trimmed = cwd.trim().replace(/\/+$/, "");
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * The row's workspace root when it names a project directory, else null.
 *
 * A project always lives UNDER a root, so a single-segment path is the root
 * itself. That is the shape the sprites provider stores (the guest home) and it is
 * the same for every project on the runtime, which is what made it useless as
 * an identity in the first place.
 */
export function namedWorkspaceRoot(workspaceRoot: string | null): string | null {
  const normalized = normalizePath(workspaceRoot ?? undefined);
  if (!normalized) return null;
  return segmentsOf(normalized).length > 1 ? normalized : null;
}

function segmentsOf(path: string): readonly string[] {
  return path
    .split("/")
    .flatMap((part) => part.split("\\"))
    .filter((segment) => segment.length > 0);
}

export function directoryNameOf(cwd: string | undefined): string | null {
  if (!cwd) return null;
  return segmentsOf(cwd).at(-1) ?? null;
}

/**
 * EVERY project resident on a runtime, for the callers that really do mean
 * "any project here": the sidebar's cloud group keeps a shell for each one so a
 * reconnect does not drop a project the user can still see.
 *
 * Never use this to answer "which project did the user pick" — that is
 * Checkout resolution's `resolveCloudRowProjectRefForIdentity`
 * (`cmmd/cmmdCheckoutResolution.ts`), and taking an element by position here
 * is exactly what FOR-36358 was.
 */
export function resolveCloudRowResolvedRefs(
  environmentStateById: Readonly<Record<string, CloudEnvironmentProjectSlice | undefined>>,
  runtimeId: string,
): readonly { readonly environmentId: EnvironmentId; readonly projectId: ProjectId }[] {
  const envId = buildCmmdRuntimeEnvironmentId(runtimeId) as EnvironmentId;
  const envState = environmentStateById[envId];
  if (!envState) return [];
  return envState.projectIds.flatMap((projectId) => {
    const project = envState.projectById[projectId as ProjectId];
    return project ? [{ environmentId: envId, projectId: project.id as ProjectId }] : [];
  });
}

// ── Cloud group state ──────────────────────────────────────────────────────────

/**
 * Logical rendering state of the Cloud Projects group.
 *
 * Priority (applied top-to-bottom):
 *   1. loading + no rows   → show skeleton
 *   2. error + no rows     → show error-no-rows (distinct from empty)
 *   3. error + stale rows  → show rows + stale-offline indicator
 *   4. rows (happy path)   → show rows (optionally stale marker)
 *   5. empty (no error)    → show empty copy
 */
export type CloudGroupState =
  | { readonly kind: "loading" }
  | { readonly kind: "error-no-rows"; readonly message: string }
  | { readonly kind: "stale-rows"; readonly message: string }
  | { readonly kind: "rows" }
  | { readonly kind: "empty" };

export function resolveCloudGroupState(
  snapshot: Pick<
    CmmdForgeRuntimeListSnapshot,
    "isLoading" | "error" | "rows" | "stale" | "startupPhase"
  >,
): CloudGroupState {
  const hasRows = snapshot.rows.length > 0;

  if ((snapshot.isLoading || snapshot.startupPhase === "auth-refreshing") && !hasRows) {
    return { kind: "loading" };
  }
  if (snapshot.error && !hasRows) {
    return { kind: "error-no-rows", message: snapshot.error };
  }
  if (snapshot.error && hasRows) {
    return { kind: "stale-rows", message: snapshot.error };
  }
  if (hasRows) {
    return { kind: "rows" };
  }
  return { kind: "empty" };
}
